// Position sizing math for the calculator modal. Ported from a standalone
// sizer that ran this as an Express endpoint; here it is a pure function, since
// the only thing that ever needed the network was the quote, and StockService
// already fetches that. No DTO/status envelope — the modal renders the result
// directly.

export const DEFAULT_PORTFOLIO = 250_000
export const DEFAULT_ALLOC_PCT = 3
export const DEFAULT_RISK_PCT = 5

export type PositionSizeInput = {
  // null means "left blank" and falls back to the default; NaN means the user
  // typed something that is not a number, which is an error. `last` is the only
  // field where blank is legitimate — it just leaves the readouts empty.
  portfolio: number | null
  allocPct: number | null
  riskPct: number | null
  last: number | null
  lastTyped?: boolean
}

export type SizedPosition = {
  portfolio: number
  allocPct: number
  riskPct: number
  last: number | null
  shares: number | null
  positionValue: number | null
  stop: number | null
  dollarRisk: number | null
  note: string
}

export type PositionSizeResult = SizedPosition | { error: string }

export const EMPTY_NOTE =
  'Type a symbol to pull its price, or enter a buy price yourself.'

function round2 (n: number): number {
  return Math.round(n * 100) / 100
}

function dollars (n: number): string {
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

/**
 * Reads a money/percent field the way the inputs render it: `$250,000` and
 * `1,234.56` are numbers, blank is null, and anything else comes back NaN so
 * the caller can tell "empty" apart from "garbage".
 */
export function parseMoney (raw: string): number | null {
  const trimmed = raw.trim().replace(/[$,]/g, '')
  if (!trimmed) return null
  return Number(trimmed)
}

export function computePositionSize (
  input: PositionSizeInput
): PositionSizeResult {
  const fields: [number | null | undefined, string][] = [
    [input.portfolio, 'Portfolio'],
    [input.allocPct, 'Alloc %'],
    [input.riskPct, 'Risk %'],
    [input.last, 'Last']
  ]
  for (const [value, label] of fields) {
    if (value != null && !Number.isFinite(value)) {
      return { error: `${label} must be a number. Fix the field and try again.` }
    }
  }

  const portfolio = input.portfolio ?? DEFAULT_PORTFOLIO
  const allocPct = input.allocPct ?? DEFAULT_ALLOC_PCT
  const riskPct = input.riskPct ?? DEFAULT_RISK_PCT

  if (portfolio <= 0) {
    return { error: 'Portfolio must be greater than 0. Enter account size and try again.' }
  }
  if (allocPct <= 0 || allocPct > 100) {
    return { error: 'Alloc % must be between 0 and 100, not including 0. Fix alloc % and try again.' }
  }
  if (riskPct <= 0 || riskPct >= 100) {
    return { error: 'Risk % is percent of the buy (last) to the stop. Use more than 0 and less than 100.' }
  }
  if (input.last != null && input.last <= 0) {
    return { error: 'Last must be greater than 0. Type a price or Enter on the stock to load last.' }
  }

  const base = { portfolio, allocPct, riskPct }

  if (input.last == null) {
    return {
      ...base,
      last: null,
      shares: null,
      positionValue: null,
      stop: null,
      dollarRisk: null,
      note: EMPTY_NOTE
    }
  }

  const last = round2(input.last)
  const budget = portfolio * (allocPct / 100)
  // A partial share is not orderable, so floor. The epsilon keeps a budget that
  // divides evenly from falling a share short on binary rounding.
  const shares = Math.floor(budget / last + 1e-12)
  const positionValue = round2(shares * last)
  const stop = round2(last * (1 - riskPct / 100))
  const dollarRisk = shares >= 1 ? round2(shares * (last - stop)) : null

  // Wording tracks the field labels — account size, allocation, buy price — so
  // the sentence names the same things the inputs do.
  const lastPhrase = input.lastTyped
    ? `your ${dollars(last)} buy price`
    : `the ${dollars(last)} last trade`
  const note =
    shares < 1
      ? `${allocPct}% of your account does not cover one share at ${dollars(last)}. Raise the account size or allocation, or pick a cheaper symbol.`
      : `${shares} share${shares === 1 ? '' : 's'} = ${allocPct}% of your account. Stop ${dollars(stop)} is ${riskPct}% below ${lastPhrase}.`

  return { ...base, last, shares, positionValue, stop, dollarRisk, note }
}
